import type { ReactElement } from 'react'
import { useState } from 'react'
import { Temporal } from 'temporal-polyfill'
import { getKpiPeriods, useKpis } from '~/api/queries/kpis'
import { KpiCard } from '~/components/Global/Blocks/Kpi/KpiCard'
import { TrendIndicator } from '~/components/Global/Blocks/Kpi/TrendIndicator'
import { formatDurationSeconds } from '~/utils/date'
import { kpisRow } from './ApplicationsKpis.css'

const CURRENT_KEYS = [
  'applications.received',
  'applications.completed',
  'applications.processing_time',
  'applications.top_sources',
  'offers.active'
] as const

const PREVIOUS_KEYS = [
  'applications.received',
  'applications.completed',
  'applications.processing_time',
  'offers.active'
] as const

export const ApplicationsKpis = (): ReactElement => {
  const [today] = useState(() => Temporal.Now.plainDateISO())
  const periods = getKpiPeriods(today)
  const { data: current, isPending } = useKpis(CURRENT_KEYS, periods.current)
  const { data: previous } = useKpis(PREVIOUS_KEYS, periods.previous)

  const topSource = current?.['applications.top_sources'].items[0]

  return (
    <div className={kpisRow}>
      <KpiCard
        isLoading={isPending}
        label="Candidatures reçues (7 j)"
        info="Candidatures reçues, hors spam."
        value={current?.['applications.received'].value ?? '-'}
        trend={
          <TrendIndicator
            current={current?.['applications.received'].value}
            previous={previous?.['applications.received'].value}
          />
        }
      />
      <KpiCard
        isLoading={isPending}
        label="Candidatures traitées (7 j)"
        info="Candidatures traitées, refus compris, quelle que soit leur date de réception."
        value={current?.['applications.completed'].value ?? '-'}
        trend={
          <TrendIndicator
            current={current?.['applications.completed'].value}
            previous={previous?.['applications.completed'].value}
          />
        }
      />
      <KpiCard
        isLoading={isPending}
        label="Temps de traitement (7 j)"
        info="Délai moyen entre la réception et le traitement d'une candidature."
        value={
          current
            ? formatDurationSeconds(
                current['applications.processing_time'].value
              )
            : '-'
        }
        trend={
          <TrendIndicator
            current={current?.['applications.processing_time'].value}
            previous={previous?.['applications.processing_time'].value}
            invert
          />
        }
      />
      <KpiCard
        isLoading={isPending}
        label="Top source (7 j)"
        info="Source ayant apporté le plus de candidatures, avec leur nombre."
        value={topSource ? `${topSource.name} (${topSource.count})` : '-'}
      />
      <KpiCard
        isLoading={isPending}
        label="Offres actives (7 j)"
        info="Offres ayant reçu au moins une candidature. Les candidatures spontanées ne sont pas comptées."
        value={current?.['offers.active'].value ?? '-'}
        trend={
          <TrendIndicator
            current={current?.['offers.active'].value}
            previous={previous?.['offers.active'].value}
          />
        }
      />
    </div>
  )
}
